import React, { useState, useRef, useEffect, useMemo, useContext, useCallback } from "react";
import { Box, IconButton, CircularProgress, Menu, MenuItem, ListSubheader, Typography, Divider } from "@mui/material";
import { ExpandMore, Download, Upload, Close, Delete, AttachFile } from "@mui/icons-material";
import { UserContext } from "../../../../../../../hooks/UserContext";
import { MRT_Row, MRT_ColumnDef } from "material-react-table";
import { FeatureCollection } from "geojson";
import type { Job, Deal } from "../../../../../../../models";
import { ImportTableColumn } from "../../../../../../../utilities/types";
import {
	ValidationContext,
	isFieldRequired,
	isFieldVisible,
	ZONE_CORE_ERROR,
	GRID_CENTROID_ERROR,
} from "../../../../../../../utilities/jobValidation";
import { normalizePointsFC } from "../../../../../../../domain/sample/sampleService";
import { columnDisplayNames } from "../../../../../../admin/ImportSettings";
import { GeoJSONOptionsKey, GeoJSONFileKey } from "../../../ImportTable.types";
import { PortalTooltip } from "../../../../../../helpers";
import { ColumnContext } from "../ColumnContext";
import { useGeoJSONFileColumnViewModel } from "./geoJSONFileColumn.vm";
import { GeoJSONFileColumnViewModel, DOWNLOAD_FORMAT_OPTIONS, PendingPointsUpload } from "./geoJSONFileColumn.types";
import { useValidationErrors } from "../../../shared/hooks/ValidationErrorsContext";
import { SampleIdPropertyDialog } from "../../../../../shared/SampleIdPropertyDialog";

// Re-export for backward compatibility
export { geoJSONKeyToOptionType } from "./geoJSONFileColumn.types";

interface GeoJSONFileColumnCellProps {
	row: MRT_Row<Job>;
	vm: GeoJSONFileColumnViewModel;
	optionsKey: GeoJSONOptionsKey;
	geoJSONKey: GeoJSONFileKey;
	columnId: ImportTableColumn;
	validationContext: ValidationContext;
	allDeals?: Deal[];
}

/**
 * Memoized Cell component for geoJSON file columns
 */
const GeoJSONFileColumnCell = React.memo(
	function GeoJSONFileColumnCell({
		row,
		vm,
		optionsKey,
		geoJSONKey,
		columnId,
		validationContext,
		allDeals,
	}: GeoJSONFileColumnCellProps) {
		const job = row.original;
		const jobId = job.id;
		const validationErrors = useValidationErrors();
		const validationKey = columnId;

		const [isLoading, setIsLoading] = useState(false);
		const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
		const [downloadMenuAnchorEl, setDownloadMenuAnchorEl] = useState<null | HTMLElement>(null);
		const [pendingUpload, setPendingUpload] = useState<PendingPointsUpload | null>(null);
		const fileInputRef = useRef<HTMLInputElement>(null);

		// Check if user is a superuser for format menu
		const userContext = useContext(UserContext);
		const isSuperuser = userContext?.isAdmin ?? false;
		// Track if initial auto-select has been done to prevent re-selecting after user deletes
		const hasAutoSelectedRef = useRef(false);

		// Build file validation context using centralized logic
		const deal = allDeals?.find((d) => d.id === job.dealIds?.[0]);
		const fileContext: ValidationContext = {
			...validationContext,
			job,
			deal,
			isPointsAttached: job.isPointsAttached,
		};
		const isFieldRequiredValue = isFieldRequired(columnId, fileContext);
		const isFieldHidden = !isFieldVisible(columnId, fileContext);

		// Get computed data from ViewModel
		const availableOptions = useMemo(() => vm.getAvailableOptions(job, optionsKey), [job.fileOptions, optionsKey]);

		// Get currently selected GeoJSON
		const currentGeoJSON = job.files?.[geoJSONKey] as FeatureCollection | undefined;

		// Find the matching option for the current selection
		const selectedOption = useMemo(
			() => vm.getSelectedOption(availableOptions, currentGeoJSON),
			[availableOptions, currentGeoJSON],
		);

		// Validation error state
		const errorKey = `${validationKey}-${jobId}`;
		const hasError = !!validationErrors[errorKey];
		const errorMessage = validationErrors[errorKey];
		const isPointTypeError = errorMessage === ZONE_CORE_ERROR || errorMessage === GRID_CENTROID_ERROR;

		// Auto-select single option only on initial mount (not after user deletes)
		// NOTE: These hooks must be called unconditionally (before any early returns)
		useEffect(() => {
			// Skip if we've already auto-selected for this component instance
			if (hasAutoSelectedRef.current) return;

			if (availableOptions.length === 1 && !currentGeoJSON && !isFieldHidden) {
				hasAutoSelectedRef.current = true;
				vm.updateJobGeoJSON(jobId, geoJSONKey, availableOptions[0].option.geoJSON);
			}
		}, [availableOptions, currentGeoJSON, isFieldHidden, jobId, geoJSONKey, vm]);

		// Mark as auto-selected if user already has a file (prevents re-selecting after delete)
		useEffect(() => {
			if (currentGeoJSON) {
				hasAutoSelectedRef.current = true;
			}
		}, [currentGeoJSON]);

		// Group options for menu display (must be called unconditionally before early return)
		const groupedMenuOptions = useMemo(() => vm.getGroupedMenuOptions(availableOptions), [availableOptions, vm]);

		// Menu handlers
		const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setMenuAnchorEl(event.currentTarget);
		const handleMenuClose = () => setMenuAnchorEl(null);

		// File input reset helper
		const resetFileInput = () => {
			if (fileInputRef.current) fileInputRef.current.value = "";
		};

		// Sample ID dialog handlers
		const handleSampleIdConfirm = useCallback(
			(key: string) => {
				if (!pendingUpload) return;
				const normalized = normalizePointsFC(pendingUpload.geoJSON, { sampleIdKey: key });
				vm.updateJobWithUpload(
					pendingUpload.jobId,
					pendingUpload.optionsKey,
					pendingUpload.geoJSONKey,
					normalized,
					pendingUpload.fileName,
				);
				setPendingUpload(null);
			},
			[pendingUpload, vm],
		);

		const handleSampleIdSkip = useCallback(() => {
			if (!pendingUpload) return;
			const normalized = normalizePointsFC(pendingUpload.geoJSON);
			vm.updateJobWithUpload(
				pendingUpload.jobId,
				pendingUpload.optionsKey,
				pendingUpload.geoJSONKey,
				normalized,
				pendingUpload.fileName,
			);
			setPendingUpload(null);
		}, [pendingUpload, vm]);

		// Render content based on option count
		const optionCount = availableOptions.length;

		return (
			<Box
				display="flex"
				flexDirection={isPointTypeError ? "column" : "row"}
				alignItems={isPointTypeError ? "flex-start" : "center"}
				height="100%"
				width="100%"
				sx={{ opacity: isFieldHidden ? 0.5 : 1 }}
			>
				{/* Case 0: No options - upload button */}
				{optionCount === 0 && (
					<>
						<input
							ref={fileInputRef}
							type="file"
							accept=".zip,.json,.geojson,.kml,.kmz,.shp,.shx,.dbf,.prj"
							style={{ display: "none" }}
							onChange={(event) => {
								const file = event.target.files?.[0];
								if (file) {
									vm.handleUpload(jobId, optionsKey, geoJSONKey, file, setIsLoading, resetFileInput, setPendingUpload);
								}
							}}
							disabled={isFieldHidden || isLoading}
						/>
						<Box display="flex" alignItems="center" width="100%">
							<Box>
								<PortalTooltip title="Upload file">
									<span>
										<IconButton
											size="small"
											onClick={() => fileInputRef.current?.click()}
											disabled={isFieldHidden || isLoading}
										>
											{isLoading ? <CircularProgress size={18} /> : <Upload fontSize="small" />}
										</IconButton>
									</span>
								</PortalTooltip>
							</Box>
							<Box flex={1} minWidth={0}>
								{isFieldRequiredValue && (
									<Typography noWrap color={hasError ? "error" : "text.secondary"} variant="body2">
										(required)
									</Typography>
								)}
							</Box>
						</Box>
					</>
				)}
				{/* Case 1: Single option - AttachFile (download) + Delete icons */}
				{optionCount === 1 && (
					<Box display="flex" alignItems="center" gap={0.5}>
						<PortalTooltip
							title={
								<>
									{availableOptions[0].option.name}
									<br />
									{isSuperuser ? "Click to choose download format" : "Click to download as a shapefile"}
								</>
							}
						>
							<span>
								{isSuperuser ? (
									// Superuser: Download button opens format menu
									<IconButton
										size="small"
										onClick={(e) => setDownloadMenuAnchorEl(e.currentTarget)}
										disabled={isFieldHidden || isLoading || !currentGeoJSON}
									>
										{isLoading ? <CircularProgress size={18} /> : <AttachFile fontSize="small" />}
									</IconButton>
								) : (
									// Non-superuser: Simple download (existing behavior)
									<IconButton
										size="small"
										onClick={() => {
											if (currentGeoJSON && selectedOption) {
												vm.handleDownload(currentGeoJSON, selectedOption, setIsLoading);
											}
										}}
										disabled={isFieldHidden || isLoading || !currentGeoJSON}
									>
										{isLoading ? <CircularProgress size={18} /> : <AttachFile fontSize="small" />}
									</IconButton>
								)}
							</span>
						</PortalTooltip>

						{/* Format selection menu for superusers */}
						{isSuperuser && (
							<Menu
								anchorEl={downloadMenuAnchorEl}
								open={Boolean(downloadMenuAnchorEl)}
								onClose={() => setDownloadMenuAnchorEl(null)}
							>
								{DOWNLOAD_FORMAT_OPTIONS.map((opt) => (
									<MenuItem
										key={opt.value}
										onClick={() => {
											if (currentGeoJSON && selectedOption) {
												vm.handleDownload(currentGeoJSON, selectedOption, setIsLoading, opt.value);
											}
											setDownloadMenuAnchorEl(null);
										}}
									>
										{opt.label}
									</MenuItem>
								))}
							</Menu>
						)}

						{currentGeoJSON && (
							<PortalTooltip title="Delete">
								<span>
									<IconButton
										size="small"
										onClick={() => vm.handleDelete(jobId, optionsKey, geoJSONKey, currentGeoJSON)}
										disabled={isFieldHidden || isLoading}
									>
										<Delete fontSize="small" />
									</IconButton>
								</span>
							</PortalTooltip>
						)}
					</Box>
				)}
				{/* Case 2+: Multiple options - icon changes based on selection state */}
				{optionCount >= 2 && (
					<>
						<Box display="flex" alignItems="center" gap={0.5}>
							{/* Icon changes: AttachFile when selected, ExpandMore when not */}
							<PortalTooltip
								title={
									currentGeoJSON && selectedOption ? (
										<>
											{selectedOption.option.name}
											<br />
											Click to view options
										</>
									) : (
										"Select file"
									)
								}
							>
								<span>
									<IconButton size="small" onClick={handleMenuOpen} disabled={isFieldHidden}>
										{currentGeoJSON ? <AttachFile fontSize="small" /> : <ExpandMore fontSize="small" />}
									</IconButton>
								</span>
							</PortalTooltip>

							{currentGeoJSON && (
								<PortalTooltip title="Clear selection">
									<span>
										<IconButton
											size="small"
											onClick={() => vm.updateJobGeoJSON(jobId, geoJSONKey, undefined)}
											disabled={isFieldHidden || isLoading}
										>
											<Close fontSize="small" />
										</IconButton>
									</span>
								</PortalTooltip>
							)}
						</Box>

						<Menu
							anchorEl={menuAnchorEl}
							open={Boolean(menuAnchorEl)}
							onClose={handleMenuClose}
							slotProps={{ paper: { sx: { maxHeight: 300, minWidth: 200 } } }}
						>
							{/* Download option - only show when file is selected */}
							{currentGeoJSON &&
								selectedOption &&
								(isSuperuser
									? // Superuser: Show format options directly in menu
										[
											...DOWNLOAD_FORMAT_OPTIONS.map((opt) => (
												<MenuItem
													key={opt.value}
													onClick={() => {
														vm.handleDownload(currentGeoJSON, selectedOption, setIsLoading, opt.value);
														handleMenuClose();
													}}
												>
													<Download fontSize="small" sx={{ mr: 1 }} />
													{opt.label}
												</MenuItem>
											)),
											<Divider key="divider" />,
										]
									: // Non-superuser: Single download option
										[
											<MenuItem
												key="download"
												onClick={() => {
													vm.handleDownload(currentGeoJSON, selectedOption, setIsLoading);
													handleMenuClose();
												}}
											>
												<Download fontSize="small" sx={{ mr: 1 }} />
												Download as shapefile
											</MenuItem>,
											<Divider key="divider" />,
										])}

							{/* Grouped selection options */}
							{Object.entries(groupedMenuOptions).map(([groupLabel, opts]) => [
								<ListSubheader key={`header-${groupLabel}`} sx={{ lineHeight: 2, marginLeft: 0 }}>
									{groupLabel}
								</ListSubheader>,
								...opts.map((opt) => (
									<MenuItem
										key={opt.option.name}
										onClick={() => {
											vm.updateJobGeoJSON(jobId, geoJSONKey, opt.option.geoJSON);
											handleMenuClose();
										}}
										selected={selectedOption?.option.name === opt.option.name}
									>
										{opt.option.name}
									</MenuItem>
								)),
							])}
						</Menu>
					</>
				)}
				{/* Error message for point type errors */}
				{isPointTypeError && (
					<Typography variant="caption" color="error" noWrap sx={{ width: "100%" }}>
						{errorMessage}
					</Typography>
				)}

				{/* Sample ID property selection dialog for points uploads */}
				{pendingUpload && (
					<SampleIdPropertyDialog
						open={true}
						propertyKeys={pendingUpload.propertyKeys}
						onConfirm={handleSampleIdConfirm}
						onSkip={handleSampleIdSkip}
					/>
				)}
			</Box>
		);
	},
	(prevProps, nextProps) => {
		const prevJob = prevProps.row.original;
		const nextJob = nextProps.row.original;

		if (prevJob.id !== nextJob.id) return false;
		if (prevJob.files?.[prevProps.geoJSONKey] !== nextJob.files?.[nextProps.geoJSONKey]) return false;
		if (prevJob.fileOptions?.[prevProps.optionsKey] !== nextJob.fileOptions?.[nextProps.optionsKey]) return false;
		if (prevJob.dealIds?.[0] !== nextJob.dealIds?.[0]) return false;
		if (prevJob.isPointsAttached !== nextJob.isPointsAttached) return false;
		if (prevProps.columnId !== nextProps.columnId) return false;
		if (prevProps.geoJSONKey !== nextProps.geoJSONKey) return false;
		if (prevProps.optionsKey !== nextProps.optionsKey) return false;

		return true;
	},
);

/**
 * Build a GeoJSON file column
 * Uses memoized Cell components to prevent unnecessary re-renders when column definitions change
 */
export function geoJSONFileColumn(
	context: ColumnContext,
	optionsKey: GeoJSONOptionsKey,
	geoJSONKey: GeoJSONFileKey,
	columnId: ImportTableColumn,
): MRT_ColumnDef<Job, unknown> {
	const vm = useGeoJSONFileColumnViewModel(context);

	const displayName = columnDisplayNames[columnId];
	const validationKey = columnId;

	return {
		id: validationKey,
		header: displayName,
		accessorFn: (row) => row.files?.[geoJSONKey],
		enableEditing: false,
		size: 100,
		maxSize: 200,
		Cell: ({ row }: { row: MRT_Row<Job> }) => (
			<GeoJSONFileColumnCell
				row={row}
				vm={vm}
				optionsKey={optionsKey}
				geoJSONKey={geoJSONKey}
				columnId={columnId}
				validationContext={context.validationContext}
				allDeals={context.props.allDeals}
			/>
		),
	};
}
